import { useRef, useState } from "react";
import { CalendarBlankIcon } from "@phosphor-icons/react";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import PropTypes from "prop-types";

import Label from "../Label";

const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

/**
 * A month picker field with a read-only input and an overlay grid of months (Enero–Diciembre).
 * Clicking the input or the calendar icon opens a 3×4 month grid via OverlayPanel.
 *
 * @param {object} props
 * @param {string} props.id - Unique identifier for the input; used to associate the label and ARIA descriptions.
 * @param {string} [props.label] - Text displayed as the input label.
 * @param {string} [props.name] - Forwarded in the normalized onChange event as `target.name`.
 * @param {number} [props.value] - The current selected month (1–12); must be provided for controlled usage.
 * @param {boolean} [props.isRequired=false] - When true, renders a required indicator on the label and sets `aria-required` on the input.
 * @param {function} [props.onChange] - Called with a DOM-compatible event `{ target: { name, value } }` where value is the month number (1–12).
 * @param {string} [props.error] - Error message displayed below the input; also marks the input as invalid.
 * @param {string} [props.helperText] - Hint text shown below the input when there is no error.
 * @param {string} [props.className] - Additional CSS classes applied to the input group wrapper.
 */
const InputMonth = ({
  id,
  label,
  name,
  value,
  isRequired,
  onChange,
  error,
  helperText,
  className,
}) => {
  const overlayRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const listboxId = `${id}-listbox`;
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const describedBy =
    [error ? errorId : null, !error && helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const selectedMonth = MONTHS.find((m) => m.value === value);
  const displayValue = selectedMonth ? `1 de ${selectedMonth.label}` : "";

  const handleToggle = (e) => {
    overlayRef.current.toggle(e);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle(e);
    }
  };

  const handleSelect = (month) => {
    onChange({ target: { name, value: month.value } });
    overlayRef.current.hide();
    inputRef.current?.focus();
  };

  return (
    <section aria-labelledby={labelId} className="flex flex-col gap-1 w-full">
      <div id={labelId}>
        <Label htmlFor={id} text={label} isRequired={!!isRequired} />
      </div>
      <div className={`p-inputgroup flex-1 ${className ?? ""}`.trim()}>
        <InputText
          ref={inputRef}
          id={id}
          readOnly
          value={displayValue}
          aria-labelledby={labelId}
          aria-required={isRequired || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          role="combobox"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className={`!h-5 cursor-pointer${error ? " p-invalid" : ""}`}
        />
        <span
          className="p-inputgroup-addon !py-0 cursor-pointer"
          aria-hidden="true"
        >
          <CalendarBlankIcon size={20} />
        </span>
      </div>
      {!!helperText && !error && (
        <small id={helperId} className="-mt-1 text-gray-500">
          {helperText}
        </small>
      )}
      {!!error && (
        <small id={errorId} role="alert" className="-mt-1 text-red-500">
          {error}
        </small>
      )}
      <OverlayPanel
        ref={overlayRef}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
      >
        <datalist
          id={listboxId}
          aria-label={label}
          className="grid grid-cols-3 gap-1"
        >
          {MONTHS.map((month) => (
            <button
              key={month.value}
              onClick={() => handleSelect(month)}
              className={`px-3 py-1.5 rounded text-sm w-full text-center transition-colors ${
                month.value === value
                  ? "p-highlight font-semibold"
                  : "hover:bg-gray-100"
              }`}
            >
              {month.label}
            </button>
          ))}
        </datalist>
      </OverlayPanel>
    </section>
  );
};

InputMonth.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.number,
  isRequired: PropTypes.bool,
  onChange: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
};

export default InputMonth;
